<template>
  <div>
    <row>
      <col12>
        <v-card elevation="2">
          <v-card-text>
            <div class="icon-title">
              <v-icon>mdi-key-variant</v-icon>
              <b>{{ $t('myprofile.api_tokens') }}</b>
            </div>
            <p class="text-body-2 mb-4">
              {{ $t('myprofile.api_tokens_description') }}
            </p>

            <!-- Create new token -->
            <v-row class="align-center">
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="newTokenName"
                  data-testid="api-token-name-input"
                  :label="$t('myprofile.api_token_name')"
                  :hint="$t('myprofile.api_token_name_hint')"
                  persistent-hint
                  maxlength="60"
                  @keydown.enter="createToken"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-btn
                  color="primary"
                  block
                  data-testid="api-token-create-btn"
                  :loading="creating"
                  @click="createToken"
                >
                  <v-icon start>mdi-plus</v-icon>
                  {{ $t('myprofile.create_api_token') }}
                </v-btn>
              </v-col>
            </v-row>

            <!-- Newly created token (shown only once) -->
            <v-alert
              v-if="createdToken"
              type="warning"
              prominent
              class="mt-4"
              data-testid="api-token-created-alert"
            >
              <div class="mb-2">
                <strong>{{ $t('myprofile.api_token_created_once') }}</strong>
              </div>
              <div class="d-flex align-center token-reveal">
                <code class="token-code">{{ createdToken }}</code>
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  @click="copyToken(createdToken)"
                  :title="$t('myprofile.copy')"
                >
                  <v-icon>mdi-content-copy</v-icon>
                </v-btn>
              </div>
            </v-alert>

            <v-divider class="my-4"></v-divider>

            <v-alert
              v-if="tokens.length === 0"
              type="info"
              text
              data-testid="api-tokens-empty"
            >
              {{ $t('myprofile.no_api_tokens_found') }}
            </v-alert>

            <v-list v-else data-testid="api-tokens-list">
              <v-list-item
                v-for="token in tokens"
                :key="token.id"
                three-line
                :data-testid="'api-token-item-' + token.id"
              >
                <v-list-item-title>
                  {{ token.name || $t('myprofile.api_token_unnamed') }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <div class="text-caption">
                    <strong>{{ $t('myprofile.api_token_prefix') }}:</strong>
                    <code>{{ token.token_prefix }}…</code><br>
                    <strong>{{ $t('myprofile.created') }}:</strong> {{ formatDate(token.created_at) }}<br>
                    <strong>{{ $t('myprofile.last_used') }}:</strong>
                    {{ token.last_used_at ? formatDate(token.last_used_at) : $t('myprofile.api_token_never_used') }}
                  </div>
                </v-list-item-subtitle>
                <template #append>
                  <v-btn
                    color="error"
                    size="small"
                    variant="outlined"
                    data-testid="api-token-delete-btn"
                    @click="revokeToken(token.id)"
                    :loading="revoking === token.id"
                  >
                    {{ $t('myprofile.delete_api_token') }}
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </col12>
    </row>
  </div>
</template>

<script>
import Row from "@/components/layout/Row";
import Col12 from "@/components/layout/Col12";

export default {
  name: 'ApiTokens',
  components: {Row, Col12},
  data() {
    return {
      tokens: [],
      newTokenName: '',
      createdToken: null,
      creating: false,
      revoking: null,
      loading: false,
    }
  },
  async mounted() {
    await this.loadTokens()
  },
  methods: {
    async loadTokens() {
      try {
        this.loading = true
        const response = await this.$api.user.list_api_tokens()
        if (Array.isArray(response)) {
          this.tokens = response
        }
      } catch (error) {
        console.error('Error loading API tokens:', error)
        await this.$store.dispatch('snackbars/add_failed_snack', this.$t('myprofile.failed_to_load_api_tokens'))
      } finally {
        this.loading = false
      }
    },

    async createToken() {
      try {
        this.creating = true
        const response = await this.$api.user.create_api_token(this.newTokenName)
        if (response && response.token) {
          this.createdToken = response.token
          this.newTokenName = ''
          await this.$store.dispatch('snackbars/add_success_snack', this.$t('myprofile.api_token_created'))
          await this.loadTokens()
        } else {
          await this.$store.dispatch('snackbars/add_failed_snack', this.$t('myprofile.failed_to_create_api_token'))
        }
      } catch (error) {
        console.error('Error creating API token:', error)
        await this.$store.dispatch('snackbars/add_failed_snack', this.$t('myprofile.failed_to_create_api_token'))
      } finally {
        this.creating = false
      }
    },

    async revokeToken(tokenId) {
      try {
        this.revoking = tokenId
        const response = await this.$api.user.revoke_api_token(tokenId)
        if (response && response.status === 'success') {
          this.createdToken = null
          await this.$store.dispatch('snackbars/add_success_snack', this.$t('myprofile.api_token_revoked'))
          await this.loadTokens()
        } else {
          await this.$store.dispatch('snackbars/add_failed_snack', this.$t('myprofile.failed_to_revoke_api_token'))
        }
      } catch (error) {
        console.error('Error revoking API token:', error)
        await this.$store.dispatch('snackbars/add_failed_snack', this.$t('myprofile.failed_to_revoke_api_token'))
      } finally {
        this.revoking = null
      }
    },

    async copyToken(token) {
      try {
        await navigator.clipboard.writeText(token)
        await this.$store.dispatch('snackbars/add_success_snack', this.$t('myprofile.copy_success'))
      } catch (error) {
        console.error('Error copying token:', error)
      }
    },

    formatDate(dateString) {
      if (!dateString) return this.$t('myprofile.unknown')
      try {
        return new Date(dateString).toLocaleString()
      } catch (error) {
        return this.$t('myprofile.invalid_date')
      }
    }
  }
}
</script>

<style scoped>
.icon-title {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.icon-title v-icon {
  margin-right: 8px;
}

.token-reveal {
  gap: 8px;
}

.token-code {
  word-break: break-all;
  background: rgba(0, 0, 0, 0.06);
  padding: 6px 10px;
  border-radius: 4px;
  font-family: monospace;
}
</style>
